const resizeObserver = new ResizeObserver(() => onScroll())

const props = {
  started: false,
  elements: new Map(),
  required: new Set(),
  debounce: 250,
  debounceTimer: null,
  checkFrame: null
}
/* eslint-enable immutablejs/no-native-map-set */

function start() {
  props.started = true
  // The stats page scrolls the document, so its body keeps the window's height while the page's
  // content wrapper grows and shrinks inside it. Switching to a shorter report moves blocks into view
  // with no scroll at all; the wrapper's own resize is what tells a waiting placeholder to look again.
  resizeObserver.observe(document.querySelector('.-body__content') || document.body)
  document.addEventListener('scroll', onScroll, false)
  // document.defaultView.addEventListener('resize', onScroll, false)
  scheduleCheck()
}

function stop() {
  props.started = false
  if (props.checkFrame) cancelAnimationFrame(props.checkFrame)
  props.checkFrame = null
  props.elements.clear()
  props.required.clear()
  resizeObserver.disconnect()
  document.removeEventListener('scroll', onScroll, false)
  // document.defaultView.removeEventListener('resize', onScroll, false)
}

function isVisible(node) {
  if (!(node instanceof Element)) return false

  if (null === node.offsetParent) return false

  const style = window.getComputedStyle(node)
  if ('none' === style.display) return false
  if ('visible' !== style.visibility) return false

  const rect = node.getBoundingClientRect()
  const windowHeight = window.innerHeight || document.documentElement.clientHeight
  return (
    (rect.top + rect.height >= 0 && rect.top + rect.height <= windowHeight) ||
    (rect.bottom - rect.height >= 0 && rect.bottom - rect.height <= windowHeight) ||
    // taller than the window and covering it: neither edge is on screen, yet the reader is looking at it
    (rect.top < 0 && rect.bottom > windowHeight)
  )
}

function onScroll() {
  if (props.debounceTimer) clearTimeout(props.debounceTimer)

  props.debounceTimer = setTimeout(() => {
    props.debounceTimer = null
    check()
  }, props.debounce)
}

// A block that registers is checked on the next frame rather than through the scroll debounce: a
// report mounts its blocks one after another, and each registration restarting the debounce kept a
// block already in view waiting until the last one below the fold had mounted.
function scheduleCheck() {
  if (props.checkFrame) return

  props.checkFrame = requestAnimationFrame(() => {
    props.checkFrame = null
    check()
  })
}

function check() {
  const tasks = []

  const windowWidth = window.innerWidth || document.documentElement.clientWidth
  const windowHeight = window.innerHeight || document.documentElement.clientHeight

  // get element rects
  for (const [element, listeners] of props.elements) {
    const rect = element.getBoundingClientRect()
    if (
      isVisible(element) &&
      !(
        (rect.top < 0 && rect.bottom < 0) ||
        (rect.top > windowHeight && rect.bottom > windowHeight) ||
        (rect.left < 0 && rect.right < 0) ||
        (rect.left > windowWidth && rect.right > windowWidth)
      )
    ) {
      for (const listener of listeners) {
        tasks.push(() => listener(element))
      }
      props.elements.delete(element)
      props.required.delete(element)
    }
  }

  // stop if none left
  if (!props.elements.size) {
    stop()
  }

  if (tasks.length) {
    const step = () => {
      const task = tasks.shift()
      task()
      tasks.length && requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }
}

export default function onVisible(element, listener, required = false) {
  const listeners = props.elements.has(element) ? props.elements.get(element) : new Set()
  listeners.add(listener)

  props.elements.set(element, listeners)

  if (required) props.required.add(element)

  if (props.started) scheduleCheck()
  else start()
}
/* eslint-enable immutablejs/no-native-map-set */

export function renderElements(elements) {
  for (const element of elements) {
    if (!isVisible(element) || !hasListener(element)) continue

    const windowWidth = window.innerWidth || document.documentElement.clientWidth
    const windowHeight = window.innerHeight || document.documentElement.clientHeight

    const rect = element.getBoundingClientRect()
    if (
      (rect.top < 0 && rect.bottom < 0) ||
      (rect.top > windowHeight && rect.bottom > windowHeight) ||
      (rect.left < 0 && rect.right < 0) ||
      (rect.left > windowWidth && rect.right > windowWidth)
    )
      continue

    const listeners = props.elements.get(element)
    for (const listener of listeners) {
      listener(element)
    }
    props.elements.delete(element)
    props.required.delete(element)
  }
}

export function renderAll(callback) {
  if (props.started) {
    for (const [element, listeners] of props.elements) {
      for (const listener of listeners) {
        listener(element)
      }
    }
    stop()
  }
  if ('function' === typeof callback) callback()
}

export function renderAllAnimationFrame(callback) {
  if (props.started) {
    stop()

    const tasks = []
    const elements = new Map(props.elements)
    for (const [element, listeners] of elements) {
      for (const listener of listeners) {
        tasks.push(() => listener(element))
      }
    }
    if (tasks.length) {
      const step = () => {
        const task = tasks.shift()
        task()
        if (tasks.length) {
          requestAnimationFrame(step)
        } else if ('function' === typeof callback) {
          callback()
        }
      }
      requestAnimationFrame(step)
    }
  }
}
/* eslint-enable immutablejs/no-native-map-set */

export function renderRequired() {
  const tasks = []
  if (props.started) {
    for (const element of props.required) {
      const listeners = props.elements.get(element)
      for (const listener of listeners) {
        tasks.push(resolve => listener(element, resolve))
      }
      props.elements.delete(element)
    }
    props.required.clear()
    if (!props.elements.size) {
      stop()
    }
    if (tasks.length) {
      const step = resolve => {
        const task = tasks.shift()
        const promise = new Promise(r => task(r))
        promise.then(() => {
          if (tasks.length) {
            requestAnimationFrame(() => step(resolve))
          } else {
            resolve()
          }
        })
      }
      return new Promise(resolve => requestAnimationFrame(() => step(resolve)))
    }
  }
  return new Promise(resolve => resolve())
}

export function hasListener(element) {
  return props.started && props.elements.has(element)
}

export function removeListener(element) {
  if (!props.elements.has(element)) return

  props.elements.delete(element)
  props.required.delete(element)

  if (!props.elements.size) {
    stop()
  }
}

export function removeAllListeners() {
  if (props.debounceTimer) {
    clearTimeout(props.debounceTimer)
  }
  props.debounceTimer = null

  stop()
}
